/**
 * Nucleo de calculo de intervalos de periodo.
 *
 * Contem as PRIMITIVAS PURAS da fachada de periodo das TELAS
 * (`periodo.utils.ts`) — hoje usada por todas as telas E pelos relatorios,
 * que migraram para o `app-periodo-filtro`.
 *
 * REGRA DE OURO (refactor comportamento-preservante):
 * As primitivas retornam apenas os DIAS-ANCORA (start/end em 00:00), SEM
 * aplicar o ajuste final de hora do dia. A fachada aplica seu boundary de fim
 * de dia (TELA: 23:59 sem segundos, configuravel via params). NAO aplique o
 * boundary aqui — isso mudaria comportamento observavel.
 *
 * Todas as primitivas recebem um `ref: Date` ("hoje" / referencia) como
 * parametro, garantindo pureza e testabilidade (nunca chamam `new Date()`
 * internamente).
 */

/**
 * Ano-ancora usado por periodos "ate hoje" / "ate o fim do mes".
 * Historicamente 01/01/2000 em ambas as fachadas.
 */
export const ANO_ANCORA_PADRAO = 2000;

/**
 * Retorna o dia-ancora (00:00:00.000) correspondente a `d`,
 * descartando hora/minuto/segundo/milissegundo.
 */
export function inicioDoDia(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
}

/**
 * Retorna `d` no fim do dia em 23:59:59.999.
 *
 * Observacao: usado pela primitiva pura; as fachadas que precisam de um
 * boundary diferente (ex.: 23:59 sem segundos) NAO devem usar esta funcao
 * e sim montar o `end` com sua propria hora.
 */
export function fimDoDia(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
}

/**
 * Dia atual (`ref`) como par start/end de DIAS-ANCORA (ambos em 00:00).
 * O instante real de `ref` NAO e preservado aqui — quem precisar do
 * instante usa diretamente `ref` (caso do relatorio HOJE).
 */
export function hoje(ref: Date): { start: Date; end: Date } {
  return { start: inicioDoDia(ref), end: inicioDoDia(ref) };
}

/**
 * Dia anterior a `ref` como par start/end de DIAS-ANCORA (ambos em 00:00,
 * mesmo dia: ontem).
 */
export function ontem(ref: Date): { start: Date; end: Date } {
  const y = new Date(ref);
  y.setDate(y.getDate() - 1);
  return { start: inicioDoDia(y), end: inicioDoDia(y) };
}

/**
 * Ontem (start) ate hoje (end) como DIAS-ANCORA.
 */
export function ontemEHoje(ref: Date): { start: Date; end: Date } {
  const y = new Date(ref);
  y.setDate(ref.getDate() - 1);
  return { start: inicioDoDia(y), end: inicioDoDia(ref) };
}

/**
 * Semana civil (segunda 00:00 -> domingo 00:00) que contem `ref`.
 *
 * Logica historica (identica nas duas fachadas):
 * getDay(): 0=Domingo, 1=Segunda... — domingo e tratado como ULTIMO dia
 * da semana, entao recua 6 dias ate a segunda anterior.
 */
export function semanaCivil(ref: Date): { start: Date; end: Date } {
  const dayOfWeek = ref.getDay();
  const diffToMonday = dayOfWeek === 0 ? 6 : (dayOfWeek - 1);
  const monday = new Date(ref);
  monday.setDate(ref.getDate() - diffToMonday);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return { start: inicioDoDia(monday), end: inicioDoDia(sunday) };
}

/**
 * Mes atual de `ref`: dia 1 (start) -> ultimo dia do mes (end),
 * ambos como DIAS-ANCORA (00:00).
 */
export function mesAtual(ref: Date): { start: Date; end: Date } {
  const start = new Date(ref.getFullYear(), ref.getMonth(), 1, 0, 0, 0, 0);
  const end = new Date(ref.getFullYear(), ref.getMonth() + 1, 0, 0, 0, 0, 0);
  return { start, end };
}

/**
 * Mes anterior ao de `ref`: dia 1 -> ultimo dia, como DIAS-ANCORA.
 * Trata virada de ano corretamente.
 */
export function mesAnterior(ref: Date): { start: Date; end: Date } {
  const p = new Date(ref.getFullYear(), ref.getMonth() - 1, 1);
  const start = new Date(p.getFullYear(), p.getMonth(), 1, 0, 0, 0, 0);
  const end = new Date(p.getFullYear(), p.getMonth() + 1, 0, 0, 0, 0, 0);
  return { start, end };
}

/**
 * Proximo mes ao de `ref`: dia 1 -> ultimo dia, como DIAS-ANCORA.
 * Trata virada de ano corretamente.
 */
export function proximoMes(ref: Date): { start: Date; end: Date } {
  const n = new Date(ref.getFullYear(), ref.getMonth() + 1, 1);
  const start = new Date(n.getFullYear(), n.getMonth(), 1, 0, 0, 0, 0);
  const end = new Date(n.getFullYear(), n.getMonth() + 1, 0, 0, 0, 0, 0);
  return { start, end };
}

/**
 * Ultimos 30 dias (inclusivos): `ref` - 29 dias (start) -> `ref` (end),
 * ambos como DIAS-ANCORA.
 */
export function ultimos30(ref: Date): { start: Date; end: Date } {
  const ls = new Date(ref);
  ls.setDate(ref.getDate() - 29); // inclui hoje + 29 dias anteriores = 30 dias
  return { start: inicioDoDia(ls), end: inicioDoDia(ref) };
}

/**
 * Desde o ano-ancora (01/01/AAAA, default 2000) ate `ref`,
 * como DIAS-ANCORA.
 */
export function desdeAncora(ref: Date, anoAncora: number = ANO_ANCORA_PADRAO): { start: Date; end: Date } {
  const start = new Date(anoAncora, 0, 1, 0, 0, 0, 0);
  return { start, end: inicioDoDia(ref) };
}

/**
 * Desde o ano-ancora ate o ultimo dia do mes de `ref`, como DIAS-ANCORA.
 */
export function desdeAncoraAteFimDoMes(
  ref: Date,
  anoAncora: number = ANO_ANCORA_PADRAO,
): { start: Date; end: Date } {
  const start = new Date(anoAncora, 0, 1, 0, 0, 0, 0);
  const end = new Date(ref.getFullYear(), ref.getMonth() + 1, 0, 0, 0, 0, 0);
  return { start, end };
}

/**
 * Primeiro e ultimo dia de um mes/ano informados.
 *  - month: 1-12 (mes humano, NAO indice 0-based)
 *  - start: dia 1 (00:00) ; end: ultimo dia do mes (00:00, dia-ancora)
 *
 * Equivalente ao `firstAndLastOfMonth` historico, porem sem fixar a hora
 * de fim (a fachada aplica seu boundary).
 */
export function intervaloDeMes(month: number, year: number): { start: Date; end: Date } {
  const start = new Date(year, month - 1, 1, 0, 0, 0, 0);
  const end = new Date(year, month, 0, 0, 0, 0, 0);
  return { start, end };
}

/**
 * Ajusta `end` para 23:59:59.999 quando estiver exatamente em meia-noite
 * (00:00:00.000). Preserva instantes que ja carregam hora especifica.
 *
 * Boundary historico do relatorio (atualmente sem consumidor em producao).
 */
export function ajustarFimDoDia(end: Date): Date {
  if (
    end.getHours() === 0 &&
    end.getMinutes() === 0 &&
    end.getSeconds() === 0 &&
    end.getMilliseconds() === 0
  ) {
    const ajustado = new Date(end);
    ajustado.setHours(23, 59, 59, 999);
    return ajustado;
  }
  return end;
}

/**
 * Aplica uma hora de inicio de dia a um dia-ancora.
 * Util para as fachadas montarem o `start` com hora configuravel.
 */
export function comHoraInicio(d: Date, hour: number, minute: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), hour, minute);
}

/**
 * Aplica uma hora de fim de dia a um dia-ancora.
 * Util para as fachadas montarem o `end` com hora configuravel
 * (a TELA usa 23:59 sem segundos).
 */
export function comHoraFim(d: Date, hour: number, minute: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), hour, minute);
}
